import { AbstractControl } from '@angular/forms';
import { PostalAddress } from '@app/sales/modules/details/models/customer-response.model';
import { Bundle, MbbRouter } from '@app/sales/modules/mbb/models/mbb-offerings-response.model';
import {
  CustomerEquipmentUpgradeAgreement,
  RedemptionStatus,
} from '@app/sales/modules/mobilephone/models/equipment-upgrade-agreement.model';
import { SubscriptionHelperService } from '@app/sales/services/subscription-helper.service';
import { VoiceProduct } from '@app/shared/models/voice-product.model';

export function distinctValueFilter<Type>(value: Type, index: number, self: Type[]): boolean {
  return self.indexOf(value) === index;
}

export function buildWebShopLink(baseUrl: string, slug: string, manufacturer: string): string {
  if (!slug || !manufacturer) {
    return '';
  }

  return `${baseUrl}/mobiltelefoner/${manufacturer}/${slug}`;
}

export function focusHtmlElementById(id: string): void {
  const element = document.getElementById(id);
  if (element) {
    element.focus();
  }
}

export function disableHtmlElementById(id: string): void {
  const element = document.getElementById(id);
  if (element) {
    element.setAttribute('disabled', 'true');
  }
}

export function enableHtmlElementById(id: string): void {
  const element = document.getElementById(id);
  if (element) {
    element.removeAttribute('disabled');
  }
}

export function getCorrelatedBundle(router: MbbRouter, idFromChosenMBBSub: string): Bundle {
  if (!idFromChosenMBBSub) return;
  if (!router.bundles) return;

  const currentBundle = router.bundles.find((bundle: Bundle) => bundle.subscriptionOfferingId === idFromChosenMBBSub);

  return currentBundle;
}

export function transformAddress(address: PostalAddress): string {
  if (address.address && address.address2) {
    return `${address.address}, ${address.address2}`;
  } else if (address.address) {
    return address.address;
  } else if (address.address2) {
    return address.address2;
  } else {
    return '';
  }
}

export function hasVoiceSubscriptionChanged(product: VoiceProduct): boolean {
  return !!(
    product &&
    product.subscriber &&
    product.isExistingSubscription &&
    (product.offering?.id ||
      SubscriptionHelperService.getChangedServicesOnExistingVoiceProduct(product).length > 0 ||
      VoiceProduct.hasSubscriberChanged(product) ||
      product.replaceVoiceSimIcc ||
      product.replaceVoiceESim ||
      product.replaceDataSimIcc ||
      product.replaceTwinSimIcc)
  );
}

export function updateFormAfterAsyncValidation(control: AbstractControl): void {
  // Ensure that the form is updated after the async validation completes
  // Without setTimeout, the form is still in the "PENDING" state
  setTimeout(() => {
    control.parent?.updateValueAndValidity();
  }, 0);
}

export function removeSpacesFromString(value: string): string {
  if (!value) {
    return value;
  }
  return value.replace(/\s+/g, '');
}

export function capitalizeString(value: string, delimiters: string[] = []): string {
  if (!!value) {
    const capitalizedString = value
      .trim()
      .split('')
      .reduce((accString, char, index) => {
        if (index === 0) return char.toUpperCase();

        const previousChar = accString.charAt(index - 1);
        if (delimiters.includes(previousChar)) {
          return accString + char.toUpperCase();
        } else {
          return accString + char;
        }
      }, '');

    return capitalizedString;
  }

  return value;
}

export function isMobilBytteAgreementEligibleForBytteBytte(agreement: CustomerEquipmentUpgradeAgreement): boolean {
  return (
    agreement.canBeUpgraded &&
    agreement.remainingNumberOfPeriodsUntilUpgrade <= 0 &&
    !agreement.terminatedDate &&
    !agreement.pauseDate &&
    (agreement.redemptionStatus === null ||
      !(
        agreement.redemptionStatus === RedemptionStatus.Completed ||
        agreement.redemptionStatus === RedemptionStatus.PendingFinalGrading ||
        agreement.redemptionStatus === RedemptionStatus.Scheduled
      ))
  );
}

// We define lodash's get function here to avoid importing the entire library
// https://lodash.com/docs/4.17.15#get
export function getObjectPropertyFromPath(obj: Object, path: string) {
  return path?.split('.').reduce((acc, part) => acc && acc[part], obj) ?? obj;
}

/**
 *
 * @param x Object a
 * @param y Object b
 * @returns boolean indicating if objects are deeply equal
 */
export function deepEqual(x, y) {
  return x && y && typeof x === 'object' && typeof y === 'object'
    ? Object.keys(x).length === Object.keys(y).length &&
        Object.keys(x).reduce(function (isEqual, key) {
          return isEqual && deepEqual(x[key], y[key]);
        }, true)
    : x === y;
}
